import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/cms/ui/dialog';
import { Textarea } from '@/components/cms/ui/textarea';
import { IconButton } from '@/components/ui/icon-button';
import { Rating } from '@/components/ui/rating';
import { FieldLabel } from '@/components/ui/text';
import LureoButton from '@/common/LureoButton';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { UserFeedbackService } from '@/modules/userFeedback';
import type { UserFeedbackSource } from '@/modules/userFeedback';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

interface UserFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Source of the feedback (e.g. 'in_cms', 'on_exit_editor'). Defaults to 'in_cms'. */
  source?: UserFeedbackSource;
  /** When provided (e.g. from editor exit flow), called with 'back' when user closes without submitting, 'submit' after submit. Use to avoid navigating away when user clicks back. */
  onCloseWithIntent?: (intent: 'back' | 'submit') => void;
}

export function UserFeedbackDialog({
  open,
  onOpenChange,
  source = 'in_cms',
  onCloseWithIntent
}: UserFeedbackDialogProps) {
  const { user } = useAuthUser();
  const services = useServices();
  const userFeedbackService = useMemo(
    () => new UserFeedbackService(services),
    [services]
  );

  const [rating, setRating] = useState<number>(0);
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFeedbackTextRequired = source === 'in_cms';
  const canSubmit =
    rating >= 1 &&
    rating <= 5 &&
    (isFeedbackTextRequired ? text.trim().length > 0 : true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await userFeedbackService.submitUserFeedback({
        rating,
        source,
        text: isFeedbackTextRequired ? text.trim() : text.trim() || null
      });

      if (result.success) {
        toast.success(result.message);
        onCloseWithIntent?.('submit');
        onOpenChange(false);
        setRating(0);
        setText('');
      } else {
        toast.error(result.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onCloseWithIntent?.('back');
      onOpenChange(false);
      setRating(0);
      setText('');
    }
  };

  if (!user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send feedback</DialogTitle>
            <DialogDescription>
              Please sign in to send feedback.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Its back button closes it with the 'back' intent, so it has no close button of its own; 672px wide, as production's */}
      <DialogContent hideCloseButton className="max-w-2xl">
        {/* Production's title bar: no line under it, a 20px title after a 20px back arrow */}
        <DialogHeader className="border-b-0 py-4 pr-6">
          <div className="flex items-center gap-4">
            <IconButton label="Close" place="dialog" onClick={handleClose} className="h-6 w-6">
              <ArrowLeftIcon className="h-5 w-5" />
            </IconButton>
            <DialogTitle className="text-lureo-20 leading-7 text-lureo-ink-subtle">Send feedback</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Rate your experience and leave your feedback.
          </DialogDescription>
        </DialogHeader>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          {/* The rating sits 44px under the title, as production's */}
          <DialogBody className="gap-5 p-6 pt-11">
            <div>
              <FieldLabel mb variant="plain" className="text-center text-lureo-ink-subtle">
                How would you rate your experience? <span className="text-lureo-coral-ink">*</span>
              </FieldLabel>
              <Rating value={rating} onChange={setRating} label="Rating 1 to 5 stars" className="justify-center" />
            </div>

            {/* Feedback text: required when opened from dashboard (in_cms), optional in editor */}
            <div>
              <FieldLabel variant="plain" htmlFor="feedback-text" className="mb-1 text-lureo-ink-subtle">
                Your feedback
                {isFeedbackTextRequired ? <span className="text-lureo-coral-ink"> *</span> : <span className="font-normal text-lureo-ink-subtlest"> (optional)</span>}
              </FieldLabel>
              <Textarea
                id="feedback-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Tell us more..."
                rows={4}
                required={isFeedbackTextRequired}
                className="min-h-[100px]"
              />
            </div>
          </DialogBody>

          {/* No line over it, and production's tall pill button */}
          <DialogFooter className="border-t-0 p-6">
            <LureoButton
              type="submit"
              variant="secondary"
              suppressTooltip
              disabled={!canSubmit || isSubmitting}
              className="h-12 px-6 rounded-lureo-radius-button-pill"
            >
              {isSubmitting ? 'Submitting...' : 'Submit feedback'}
            </LureoButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
